import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import { Slider } from './ui/slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Input } from './ui/input';
import {
  Trees, Play, Square, X, Search, Loader2, ExternalLink, Star, RotateCcw,
  Layers, Save, Trash2,
} from 'lucide-react';
import AudioEffectsManager from '../utils/AudioEffectsManager';
import {
  getLastAmbient, setLastCurated, setLastLibrary, clearLastAmbient,
  getFavorites, isFavorite, toggleFavorite,
  getMixes, saveMix, deleteMix,
  fetchRemotePrefs, scheduleRemoteSave,
} from '../utils/ambientPreferences';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Ambient Scene Quick-Launcher — pinned on the Bible reader toolbar.
 *
 * Two modes inside the popover:
 *   1. Curated — 10 procedural scene presets (wilderness, garden, storm…)
 *   2. Library — Freesound.org CC0/CC-BY field recordings, streamed via a
 *      CORS-safe backend proxy (`/api/ambient-sounds/*`).
 *
 * Only one source plays at a time; starting a library track stops any
 * curated scene and vice versa. All audio stops on unmount.
 */
const AmbientSceneLauncher = ({ language = 'en' }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('curated');
  // Per-layer volumes — kept in sync to act as a single master when only one
  // layer is active, but each can be tuned independently in Layer mode.
  const [curatedVolume, setCuratedVolume] = useState(0.35);
  const [libVolume, setLibVolume] = useState(0.35);

  // Curated scene state
  const [activeSceneId, setActiveSceneId] = useState(null);
  const managerRef = useRef(null);
  if (!managerRef.current && typeof window !== 'undefined') {
    managerRef.current = new AudioEffectsManager();
  }

  // Library (Freesound) state
  const [libQuery, setLibQuery] = useState('');
  const [libResults, setLibResults] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libError, setLibError] = useState(null);
  const [activeLibId, setActiveLibId] = useState(null);
  const [activeLibSound, setActiveLibSound] = useState(null);
  const libAudioRef = useRef(null);

  // Persisted preferences (localStorage + server sync if logged in)
  const [lastAmbient, setLastAmbient] = useState(() => getLastAmbient());
  const [favorites, setFavorites] = useState(() => getFavorites());
  const [mixes, setMixes] = useState(() => getMixes());
  const [showSaveMix, setShowSaveMix] = useState(false);
  const [newMixName, setNewMixName] = useState('');

  // Layer mode: when ON, picking a Curated scene + a Library sound stack
  // simultaneously instead of one stopping the other (lets users build
  // multi-layer mixes from scratch).
  const [layerMode, setLayerMode] = useState(false);

  // Pull remote prefs once on first popover open (best-effort — quietly skips
  // for anonymous users). Local UI is rendered immediately from localStorage.
  const remoteFetchedRef = useRef(false);
  useEffect(() => {
    if (!open || remoteFetchedRef.current) return;
    remoteFetchedRef.current = true;
    (async () => {
      const remote = await fetchRemotePrefs();
      if (!remote) return; // anonymous or offline
      setLastAmbient(getLastAmbient());
      setFavorites(getFavorites());
      setMixes(getMixes());
    })();
  }, [open]);

  /* ------------------------ effects ------------------------ */
  useEffect(() => {
    managerRef.current?.setEffectVolume(curatedVolume);
  }, [curatedVolume]);

  useEffect(() => {
    if (libAudioRef.current) libAudioRef.current.volume = libVolume;
  }, [libVolume]);

  useEffect(() => () => {
    try { managerRef.current?.stopAllEffects(); } catch (_) { /* ignore */ }
    if (libAudioRef.current) {
      libAudioRef.current.pause();
      libAudioRef.current.src = '';
    }
  }, []);

  /* ------------------------ curated control ------------------------ */
  const scenes = managerRef.current?.getScenePresets() || [];

  const stopLibAudio = () => {
    if (libAudioRef.current) {
      libAudioRef.current.pause();
      libAudioRef.current.src = '';
    }
    setActiveLibId(null);
    setActiveLibSound(null);
  };

  const playScene = (sceneId, { keepLib } = {}) => {
    const stack = keepLib !== undefined ? keepLib : layerMode;
    if (!stack) stopLibAudio();
    const mgr = managerRef.current;
    if (!mgr) return;
    mgr.setEffectsEnabled(true);
    mgr.setEffectVolume(curatedVolume);
    mgr.playScene(sceneId);
    setActiveSceneId(sceneId);
    const label = scenes.find((s) => s.id === sceneId)?.label;
    setLastCurated(sceneId, label);
    setLastAmbient(getLastAmbient());
    scheduleRemoteSave();
  };

  const stopScene = () => {
    managerRef.current?.stopAllEffects();
    setActiveSceneId(null);
  };

  const stopAll = () => {
    stopScene();
    stopLibAudio();
  };

  /* ------------------------ library control ------------------------ */
  const runSearch = async (q) => {
    const query = (q ?? libQuery).trim();
    if (!query) return;
    setLibLoading(true);
    setLibError(null);
    try {
      const res = await axios.get(`${API}/api/ambient-sounds/search`, {
        params: { query, limit: 12 },
      });
      setLibResults(res.data.sounds || []);
      if (!res.data.sounds?.length) setLibError('No results — try a different keyword.');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Search failed';
      setLibError(msg);
      setLibResults([]);
    } finally {
      setLibLoading(false);
    }
  };

  const playLibSound = (sound, { keepCurated } = {}) => {
    const stack = keepCurated !== undefined ? keepCurated : layerMode;
    if (!stack) stopScene();
    if (libAudioRef.current) {
      libAudioRef.current.pause();
      libAudioRef.current.src = '';
    }
    const audio = new Audio(`${API}${sound.preview_url}`);
    audio.loop = true;
    audio.volume = libVolume;
    audio.onerror = () => {
      setLibError(`Playback error for "${sound.name.slice(0, 40)}"`);
      setActiveLibId(null);
      setActiveLibSound(null);
    };
    libAudioRef.current = audio;
    audio.play().then(() => {
      setActiveLibId(sound.id);
      setActiveLibSound(sound);
      setLastLibrary(sound);
      setLastAmbient(getLastAmbient());
      scheduleRemoteSave();
    }).catch((err) => {
      setLibError('Browser blocked audio — tap Play again');
      console.warn('Freesound play() rejected:', err);
    });
  };

  const handleToggleFavorite = (sound) => {
    const next = toggleFavorite(sound);
    setFavorites(next);
    scheduleRemoteSave();
  };

  /* ---------------- Scene Mixes (curated + library together) ---------------- */
  const playMix = (mix) => {
    // Restore each layer's saved volume (fallback to current value if absent).
    if (typeof mix.curated_volume === 'number') setCuratedVolume(mix.curated_volume);
    if (typeof mix.library_volume === 'number') setLibVolume(mix.library_volume);
    if (mix.library_sound) {
      // Make sure the row is visible in the Library tab.
      setLibResults((prev) => {
        const exists = prev.some((s) => s.id === mix.library_sound.id);
        return exists ? prev : [mix.library_sound, ...prev];
      });
      playLibSound(mix.library_sound, { keepCurated: !!mix.curated_id });
    }
    if (mix.curated_id) {
      playScene(mix.curated_id, { keepLib: !!mix.library_sound });
    }
  };

  const handleSaveMix = () => {
    const name = newMixName.trim();
    if (!name) return;
    const mix = {
      name,
      curated_id: activeSceneId || null,
      curated_label: scenes.find((s) => s.id === activeSceneId)?.label || null,
      curated_volume: activeSceneId ? curatedVolume : null,
      library_sound: activeLibSound || null,
      library_volume: activeLibSound ? libVolume : null,
    };
    const next = saveMix(mix);
    setMixes(next);
    scheduleRemoteSave();
    setNewMixName('');
    setShowSaveMix(false);
  };

  const handleDeleteMix = (mix_id) => {
    const next = deleteMix(mix_id);
    setMixes(next);
    scheduleRemoteSave();
  };

  const resumeLast = () => {
    if (!lastAmbient) return;
    if (lastAmbient.kind === 'curated') {
      playScene(lastAmbient.id);
    } else if (lastAmbient.kind === 'library' && lastAmbient.sound) {
      // If the Library tab hasn't been searched yet, make sure the result
      // row renders in the library panel by dropping it into results.
      setTab('library');
      setLibResults((prev) => {
        const exists = prev.some((s) => s.id === lastAmbient.sound.id);
        return exists ? prev : [lastAmbient.sound, ...prev];
      });
      playLibSound(lastAmbient.sound);
    }
  };

  const handleForgetLast = () => {
    clearLastAmbient();
    setLastAmbient(null);
    scheduleRemoteSave();
  };

  /* ------------------------ UI ------------------------ */
  // Shared row renderer so the Favorites list and the search-results list
  // have identical look-and-feel, test IDs included.
  // eslint-disable-next-line react/no-multi-comp
  const renderSoundRow = (s, isFav) => {
    const active = s.id === activeLibId;
    const favorited = isFav || isFavorite(s.id);
    return (
      <div
        key={`${isFav ? 'fav-' : ''}${s.id}`}
        className={`flex items-center justify-between gap-2 px-2.5 py-2 rounded-md border text-xs ${
          active
            ? 'border-emerald-500 bg-emerald-500/10'
            : 'border-border hover:border-primary/50 hover:bg-muted/40'
        }`}
        data-testid={`ambient-launcher-lib-sound-${s.id}`}
      >
        <div className="min-w-0 flex-1">
          <p className="font-medium truncate" title={s.name}>{s.name}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {Math.round(s.duration)}s • by {s.username}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => handleToggleFavorite(s)}
            className={`p-1 rounded hover:bg-muted/60 ${
              favorited ? 'text-amber-500' : 'text-muted-foreground'
            }`}
            title={favorited ? 'Remove from favorites' : 'Add to favorites'}
            data-testid={`ambient-launcher-lib-fav-${s.id}`}
          >
            <Star className={`w-3.5 h-3.5 ${favorited ? 'fill-current' : ''}`} />
          </button>
          <a
            href={s.freesound_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 rounded hover:bg-muted/60 text-muted-foreground"
            title="View on Freesound"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
          <Button
            size="sm"
            variant={active ? 'default' : 'outline'}
            className={`h-7 w-7 p-0 ${active ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
            onClick={() => (active ? stopLibAudio() : playLibSound(s))}
            data-testid={`ambient-launcher-lib-play-${s.id}`}
          >
            {active ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
    );
  };

  const isPlaying = !!activeSceneId || !!activeLibId;
  const curatedLabel = scenes.find((s) => s.id === activeSceneId)?.label;
  const libLabel = activeLibSound?.name?.slice(0, 30);
  const activeLabel = curatedLabel || libLabel;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={isPlaying ? 'default' : 'outline'}
          size="sm"
          className={isPlaying ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
          data-testid="ambient-launcher-btn"
          title={language === 'es' ? 'Escena ambiental' : 'Ambient scene'}
        >
          <Trees className="w-4 h-4 mr-2" />
          {isPlaying
            ? (language === 'es' ? `Escena: ${activeLabel}` : `Scene: ${activeLabel}`)
            : (language === 'es' ? 'Escena' : 'Scene')}
          {isPlaying && (
            <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse" aria-hidden="true" />
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[22rem] p-4"
        align="end"
        data-testid="ambient-launcher-popover"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex flex-col">
            <span className="text-sm font-semibold">
              {language === 'es' ? 'Escena ambiental' : 'Ambient scene'}
            </span>
            <span className="text-xs text-muted-foreground">
              {language === 'es'
                ? 'Un toque para empezar un ambiente en bucle'
                : 'One tap to start a looping soundscape'}
            </span>
          </div>
          {isPlaying && (
            <Button
              size="icon"
              variant="ghost"
              onClick={stopAll}
              data-testid="ambient-launcher-stop-btn"
              title={language === 'es' ? 'Detener' : 'Stop'}
              className="h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-full mb-3">
            <TabsTrigger value="curated" data-testid="ambient-launcher-tab-curated">
              Curated
            </TabsTrigger>
            <TabsTrigger value="library" data-testid="ambient-launcher-tab-library">
              Library
            </TabsTrigger>
            <TabsTrigger value="mixes" data-testid="ambient-launcher-tab-mixes">
              Mixes
            </TabsTrigger>
          </TabsList>

          {/* Resume-last pill — one-tap restore of the most recent scene */}
          {lastAmbient && !isPlaying && (
            <div
              className="flex items-center justify-between gap-2 mb-3 px-3 py-2 rounded-md border border-indigo-300/60 bg-indigo-500/10 dark:border-indigo-700"
              data-testid="ambient-launcher-resume-pill"
            >
              <button
                onClick={resumeLast}
                className="flex items-center gap-2 min-w-0 text-left flex-1"
                data-testid="ambient-launcher-resume-btn"
              >
                <RotateCcw className="w-3.5 h-3.5 shrink-0 text-indigo-600 dark:text-indigo-300" />
                <span className="flex flex-col min-w-0">
                  <span className="text-[11px] uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                    Resume {lastAmbient.kind === 'library' ? 'library sound' : 'scene'}
                  </span>
                  <span className="text-sm truncate">
                    {lastAmbient.kind === 'library'
                      ? lastAmbient.sound.name
                      : lastAmbient.label || lastAmbient.id}
                  </span>
                </span>
              </button>
              <button
                onClick={handleForgetLast}
                className="p-1 rounded hover:bg-indigo-500/20 text-muted-foreground shrink-0"
                title="Forget"
                data-testid="ambient-launcher-forget-btn"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* -------- Curated tab -------- */}
          <TabsContent value="curated" className="space-y-3 mt-0">
            <div className="grid grid-cols-2 gap-2" data-testid="ambient-launcher-grid">
              {scenes.map((s) => {
                const active = s.id === activeSceneId;
                return (
                  <button
                    key={s.id}
                    onClick={() => (active ? stopScene() : playScene(s.id))}
                    data-testid={`ambient-launcher-scene-${s.id}`}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-left text-sm transition-all ${
                      active
                        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-base leading-none">{s.icon}</span>
                      <span className="truncate">{s.label}</span>
                    </span>
                    {active ? (
                      <Square className="w-3.5 h-3.5 flex-shrink-0" />
                    ) : (
                      <Play className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                    )}
                  </button>
                );
              })}
            </div>
          </TabsContent>

          {/* -------- Library tab (Freesound) -------- */}
          <TabsContent value="library" className="space-y-3 mt-0">
            <div className="flex gap-2">
              <Input
                value={libQuery}
                onChange={(e) => setLibQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                placeholder={language === 'es' ? 'pájaros, océano, lluvia…' : 'birds, ocean, rain…'}
                className="h-9"
                data-testid="ambient-launcher-lib-input"
              />
              <Button
                size="sm"
                onClick={() => runSearch()}
                disabled={libLoading || !libQuery.trim()}
                data-testid="ambient-launcher-lib-search-btn"
              >
                {libLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {/* Favorites — pinned at the top when the user has any */}
            {favorites.length > 0 && (
              <div className="space-y-1" data-testid="ambient-launcher-lib-favorites">
                <p className="text-[11px] uppercase tracking-wide font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                  <Star className="w-3 h-3 fill-current" />
                  Favorites
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {favorites.map((s) => renderSoundRow(s, true))}
                </div>
                <div className="border-b border-border/60 pt-1" />
              </div>
            )}

            {libError && (
              <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="ambient-launcher-lib-error">
                {libError}
              </p>
            )}

            {libResults.length > 0 && (
              <div
                className="max-h-64 overflow-y-auto space-y-1 pr-1"
                data-testid="ambient-launcher-lib-results"
              >
                {libResults.map((s) => renderSoundRow(s, false))}
              </div>
            )}

            <p className="text-[10px] text-muted-foreground italic">
              Powered by{' '}
              <a
                href="https://freesound.org"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary"
              >
                Freesound.org
              </a>{' '}
              — CC0 / CC-BY field recordings.
            </p>
          </TabsContent>

          {/* -------- Mixes tab (saved curated + library combos) -------- */}
          <TabsContent value="mixes" className="space-y-3 mt-0" data-testid="ambient-launcher-mixes">
            {isPlaying ? (
              showSaveMix ? (
                <div className="space-y-2 p-2.5 rounded-md border border-emerald-500/30 bg-emerald-500/5">
                  <p className="text-xs font-medium">Save current scene as a mix</p>
                  <p className="text-[11px] text-muted-foreground">
                    {[
                      activeSceneId && (scenes.find((s) => s.id === activeSceneId)?.label || activeSceneId),
                      activeLibSound?.name,
                    ].filter(Boolean).join(' + ') || 'Nothing playing'}
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={newMixName}
                      onChange={(e) => setNewMixName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveMix()}
                      placeholder="Mix name (e.g. 'Evening Garden')"
                      className="h-8 text-sm"
                      data-testid="ambient-launcher-mix-name-input"
                    />
                    <Button
                      size="sm"
                      onClick={handleSaveMix}
                      disabled={!newMixName.trim()}
                      data-testid="ambient-launcher-mix-save-btn"
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setShowSaveMix(false); setNewMixName(''); }}
                      data-testid="ambient-launcher-mix-cancel-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowSaveMix(true)}
                  className="w-full"
                  data-testid="ambient-launcher-mix-new-btn"
                >
                  <Save className="w-3.5 h-3.5 mr-2" />
                  Save current as mix
                </Button>
              )
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Start a curated scene and/or a Library sound, then come back here to save it as a mix.
              </p>
            )}

            {mixes.length > 0 ? (
              <div
                className="max-h-64 overflow-y-auto space-y-1 pr-1"
                data-testid="ambient-launcher-mix-list"
              >
                {mixes.map((m) => (
                  <div
                    key={m.mix_id}
                    className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-md border border-border hover:border-primary/50 hover:bg-muted/40 text-xs"
                    data-testid={`ambient-launcher-mix-${m.mix_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{m.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        {[m.curated_label || m.curated_id, m.library_sound?.name]
                          .filter(Boolean)
                          .join(' + ') || 'Empty mix'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-7 p-0"
                        onClick={() => playMix(m)}
                        data-testid={`ambient-launcher-mix-play-${m.mix_id}`}
                        title="Play mix"
                      >
                        <Play className="w-3.5 h-3.5" />
                      </Button>
                      <button
                        onClick={() => handleDeleteMix(m.mix_id)}
                        className="p-1 rounded hover:bg-muted/60 text-muted-foreground"
                        title="Delete mix"
                        data-testid={`ambient-launcher-mix-delete-${m.mix_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground italic text-center py-2">
                No saved mixes yet. Stack a curated scene with a Library sound and save the combo for one-tap recall.
              </p>
            )}
          </TabsContent>
        </Tabs>

        {/* Layer mode + Shared volume */}
        <div className="mt-3 pt-3 border-t border-border space-y-2">
          <button
            onClick={() => setLayerMode((v) => !v)}
            className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors ${
              layerMode
                ? 'bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 dark:text-emerald-300'
                : 'border border-transparent hover:bg-muted/50 text-muted-foreground'
            }`}
            data-testid="ambient-launcher-layer-toggle"
            title="Stack a curated scene with a Library sound at the same time"
          >
            <span className="flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" />
              Layer mode
            </span>
            <span
              className={`inline-block w-7 h-4 rounded-full transition-colors ${
                layerMode ? 'bg-emerald-500' : 'bg-muted-foreground/30'
              } relative`}
              aria-hidden="true"
            >
              <span
                className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                  layerMode ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </span>
          </button>

          {/* Volume — one slider when only one source plays, two when stacked. */}
          {(activeSceneId && activeLibId) ? (
            <div className="space-y-2.5" data-testid="ambient-launcher-stacked-volume">
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Trees className="w-3 h-3" />
                    Curated volume
                  </span>
                  <span className="text-xs text-muted-foreground">{Math.round(curatedVolume * 100)}%</span>
                </div>
                <Slider
                  value={[curatedVolume]}
                  onValueChange={(v) => setCuratedVolume(v[0])}
                  max={1}
                  step={0.05}
                  data-testid="ambient-launcher-volume-curated"
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Star className="w-3 h-3" />
                    Library volume
                  </span>
                  <span className="text-xs text-muted-foreground">{Math.round(libVolume * 100)}%</span>
                </div>
                <Slider
                  value={[libVolume]}
                  onValueChange={(v) => setLibVolume(v[0])}
                  max={1}
                  step={0.05}
                  data-testid="ambient-launcher-volume-library"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {language === 'es' ? 'Volumen' : 'Volume'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {Math.round((activeLibId ? libVolume : curatedVolume) * 100)}%
                </span>
              </div>
              <Slider
                value={[activeLibId ? libVolume : curatedVolume]}
                onValueChange={(v) => {
                  // When idle, keep both channels in sync so the next play
                  // session inherits the user's chosen level for either source.
                  setCuratedVolume(v[0]);
                  setLibVolume(v[0]);
                }}
                max={1}
                step={0.05}
                data-testid="ambient-launcher-volume"
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AmbientSceneLauncher;
